import type { ReactNode } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';
import styled from 'styled-components';

import type { DocumentType } from '#src/__generated__/globalTypes.ts';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { Vertical } from '#src/atoms/layout/flex/index.tsx';
import {
  BodyM,
  HeadlineL,
  HeadlineXS,
  LabelM,
} from '#src/atoms/typography/index.tsx';
import { MANGOPAY_PRIVACY_POLICY } from '#src/components/fiatWallet/CreateFiatWallet/externalLinks.ts';
import { documentNameByType } from '#src/components/fiatWallet/UnblockWallet/type.ts';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { glossary } from '#src/lib/glossary.ts';

const ItemRoot = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--unit);
  padding: var(--intermediate-unit);
  background-color: var(--c-nd-100);
  border-radius: var(--unit);
`;

const ItemContent = styled.div`
  display: flex;
  gap: var(--unit);
`;

type ItemProps = {
  title: ReactNode;
  desc?: ReactNode;
  step: number;
};
const Item = ({ title, desc, step }: ItemProps) => (
  <ItemRoot>
    <ItemContent>
      <BodyM bold color="var(--c-white)">
        {step}
      </BodyM>
      <div>
        {!!title && (
          <BodyM bold color="var(--c-white)">
            {title}
          </BodyM>
        )}
        {!!desc && <LabelM color="var(--c-nd-600)">{desc}</LabelM>}
      </div>
    </ItemContent>
  </ItemRoot>
);

const Content = styled(Vertical).attrs({ gap: 2 })`
  height: 100%;
`;
const LinkInFormattedMessage = styled.a`
  text-decoration: underline;
  color: var(--c-link);
`;

type Props = {
  documentsRequired: DocumentType[];
  onNext: () => void;
};

const messages = defineMessages({
  blockedTitle: {
    id: 'UnblockWallet.intro.title',
    defaultMessage: 'We need more information to unlock your Cash Wallet!',
  },
  renewalTitle: {
    id: 'UnblockWallet.intro.renewal.title',
    defaultMessage:
      'We need more information to maintain uninterrupted access to your Cash Wallet!',
  },
  blockedSubtitle: {
    id: 'UnblockWallet.intro.subtitle',
    defaultMessage:
      'To unlock your Cash Wallet, you need to upload the following document(s):',
  },
  renewalSubtitle: {
    id: 'UnblockWallet.intro.renewal.subtitle',
    defaultMessage: 'You need to upload the following document(s):',
  },
});

export const Intro = ({ documentsRequired, onNext }: Props) => {
  const { currentUser } = useCurrentUserContext();
  return (
    <Content>
      <HeadlineL as="h3">
        <FormattedMessage
          {...(currentUser?.mangopayUser?.blocked
            ? messages.blockedTitle
            : messages.renewalTitle)}
        />
      </HeadlineL>
      <HeadlineXS as="h6">
        <FormattedMessage
          {...(currentUser?.mangopayUser?.blocked
            ? messages.blockedSubtitle
            : messages.renewalSubtitle)}
        />
      </HeadlineXS>
      {documentsRequired.map((document, index) => {
        return (
          <Item
            key={document}
            step={index + 1}
            title={<FormattedMessage {...documentNameByType[document]} />}
          />
        );
      })}
      <LabelM color="var(--c-nd-600)">
        <FormattedMessage
          id="UnblockWallet.uploadId.privacy"
          defaultMessage="Your documents remains private and will be handled according to Mangopay’s <link>Privacy policy</link>."
          values={{
            link: (...text) => (
              <LinkInFormattedMessage
                target="_blank"
                href={MANGOPAY_PRIVACY_POLICY}
              >
                {text}
              </LinkInFormattedMessage>
            ),
          }}
        />
      </LabelM>
      <Button fullWidth color="primary" size="medium" onClick={onNext}>
        <FormattedMessage {...glossary.getStarted} />
      </Button>
    </Content>
  );
};
